import React, { useState } from "react";
import { Button, CircularProgress, Grid } from "@material-ui/core";
import BackupIcon from "@material-ui/icons/Backup";
import CheckIcon from "@material-ui/icons/Check";
import SaveIcon from "@material-ui/icons/Save";
import { ConfirmDialog } from "src/apps/commons/dataDisplays";
import { FormPaperAlt } from "src/apps/commons/papers";
import useApiAlert from "src/hooks/useApiAlert";
import useHttpErrorHandler from "src/hooks/useHttpErrorHandler";
import * as Api from "../api";
import { ISSWUploadPreviewReq, ISSWUploadPreviewResp } from "../types";
import UploadSSWForm from "./UploadSSWForm";
import UploadSSWPreview from "./UploadSSWPreview";

const UploadSSW: React.FC = _ => {
  const { setMessage } = useApiAlert();
  const { handleError } = useHttpErrorHandler();

  const [req, setReq] = useState<Partial<ISSWUploadPreviewReq>>({});
  const [resp, setResp] = useState<ISSWUploadPreviewResp | undefined>();
  const [previewing, setPreviewing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [imported, setImported] = useState(false);
  const handleUploadPreview = async () => {
    if (previewing) return;
    const { partnerId, basicViewFileId, salesViewFileId } = req;
    if (!partnerId || !basicViewFileId || !salesViewFileId) {
      setMessage(
        "error",
        "กรุณากรอกข้อมูล Partner, Basic View, Sales View ให้ครบ"
      );
      return;
    }

    try {
      setPreviewing(true);
      setResp(undefined);
      setImported(false);
      const response = await Api.uploadPreviewSSW({
        partnerId,
        basicViewFileId,
        salesViewFileId
      });
      setResp(response.value);

      const {
        timeReadExcel,
        timeValidate,
        timeMatchingRows,
        timeAdditionalFetching
      } = response.value;
      console.log("Performance Report:");
      console.log(" - read excel (ms)", timeReadExcel);
      console.log(" - validate data (ms)", timeValidate);
      console.log(" - rows matching/lookup (ms)", timeMatchingRows);
      console.log(" - fetch data for preview (ms)", timeAdditionalFetching);
    } catch (e) {
      handleError(e);
    } finally {
      setPreviewing(false);
    }
  };

  const [confirmDialogState, setConfirmDialogState] = useState<{
    open: boolean;
    onConfirm?: () => void;
  }>({ open: false });
  const handleCloseDialog = () => {
    setConfirmDialogState({ open: false, onConfirm: undefined });
  };
  const handleUploadImport = () => {
    setConfirmDialogState({
      open: true,
      onConfirm: async () => {
        if (importing) return;
        if (!resp) {
          setMessage("error", "กรุณา Upload & Preview ก่อน");
          return;
        }

        try {
          setImporting(true);
          const result = await Api.uploadImportSSW({
            partnerId: resp.partnerId,
            items: resp.items
          });
          const { insertCount, updateCount } = result.value;
          setMessage(
            "success",
            `Import SSW Products สำเร็จ (insert: ${insertCount}, update: ${updateCount})`
          );
          setImported(true);
        } catch (e) {
          handleError(e);
        } finally {
          setImporting(false);
          handleCloseDialog();
        }
      }
    });
  };

  return (
    <FormPaperAlt>
      <UploadSSWForm submitting={previewing || importing} onChanged={setReq} />

      <Grid container spacing={2} justify="flex-end">
        <Grid item>
          <Button
            variant="contained"
            color="primary"
            size="large"
            type="button"
            startIcon={
              previewing ? (
                <CircularProgress color="secondary" size={20} />
              ) : (
                <BackupIcon />
              )
            }
            disabled={previewing}
            onClick={handleUploadPreview}
          >
            Upload & Preview
          </Button>
        </Grid>
      </Grid>

      {resp && (
        <>
          <UploadSSWPreview data={resp} />

          <Grid container spacing={2} justify="flex-end">
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                size="large"
                type="button"
                startIcon={
                  importing ? (
                    <CircularProgress color="secondary" size={20} />
                  ) : imported ? (
                    <CheckIcon />
                  ) : (
                    <SaveIcon />
                  )
                }
                disabled={importing || imported || resp.errors.length > 0}
                onClick={handleUploadImport}
              >
                Import Products
              </Button>
            </Grid>
          </Grid>

          <ConfirmDialog
            title={`ยืนยันการ Import ข้อมูล SSW Products`}
            description={importing ? `กรุณารอสักครู่` : undefined}
            open={confirmDialogState.open}
            onClose={handleCloseDialog}
            onConfirm={confirmDialogState.onConfirm}
            confirming={importing}
          />
        </>
      )}
    </FormPaperAlt>
  );
};

export default UploadSSW;
